import { PaginationMeta } from "../../services/apiClient";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./Pagination";

interface TablePaginationProps {
  currentPage: number;
  pagination: PaginationMeta | undefined;
  rowCount: number;
  onPageChange: (page: number) => void;
}

const TablePagination = ({ currentPage, pagination, rowCount, onPageChange }: TablePaginationProps) => {
  const totalPages = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rowCount;
  const limit = pagination?.limit ?? 10;
  const pageStart = (currentPage - 1) * limit + 1;
  const pageEnd = Math.min(currentPage * limit, total);

  const getPages = (): (number | "…")[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const around = Array.from(
      new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1].filter((p) => p >= 1 && p <= totalPages))
    ).sort((a, b) => a - b);
    const result: (number | "…")[] = [];
    around.forEach((p, i) => {
      if (i > 0 && p - around[i - 1] > 1) result.push("…");
      result.push(p);
    });
    return result;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex w-full items-center justify-between gap-4 pt-4">
      <span className="text-[13px] text-content-label">
        Showing {pageStart}–{pageEnd} of {total}
      </span>
      <Pagination className="w-auto mx-0 justify-end">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="disabled:opacity-30"
            />
          </PaginationItem>
          {getPages().map((p, i) =>
            p === "…" ? (
              <PaginationItem key={`e${i}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={p}>
                <PaginationLink isActive={p === currentPage} onClick={() => onPageChange(p as number)}>
                  {p}
                </PaginationLink>
              </PaginationItem>
            )
          )}
          <PaginationItem>
            <PaginationNext
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="disabled:opacity-30"
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export default TablePagination;
